import { HiringFlowSectionModalProps, HiringFlowSectionType } from "@/types/hiring-flow.types";
import { Button, Input, LinkTo, Modal, Stack, Text, useModals } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function HiringFlowSectionModal({
  initialTitle = "",
  sectionType = HiringFlowSectionType.custom,
  onSave,
  ...props
}: HiringFlowSectionModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const isSystemSection = sectionType !== HiringFlowSectionType.custom;
  const localizedSystemTitle = isSystemSection ? t(`hiring_flow:section_${sectionType}`) : "";
  const sectionSchema = z.object({
    title: z.string().trim().min(1, t("errors:field_required")),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof sectionSchema>>({
    mode: "onSubmit",
    resolver: zodResolver(sectionSchema),
    defaultValues: { title: initialTitle?.trim() || localizedSystemTitle },
  });
  const title = useWatch({ control, name: "title" });
  const showTitleReset = isSystemSection && title.trim() !== localizedSystemTitle;

  const onSubmit = handleSubmit(({ title }) => {
    onSave(isSystemSection && title === localizedSystemTitle ? null : title);
    close(props.id);
  });

  return (
    <Modal
      {...props}
      title={t("common:section")}
      withCloser
      actions={
        <Stack>
          <Button type="submit" size="lg" onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          required
          label={t("common:title")}
          {...register("title")}
          error={errors.title?.message}
        />
        {showTitleReset && (
          <Stack direction="horizontal" items="center" gap="xs" className="py-2">
            <Text className="text-sm text-secondary">
              {t("hiring_flow:system_section_title_reset_hint", { title: localizedSystemTitle })}
            </Text>
            <LinkTo
              className="text-sm text-secondary"
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.currentTarget.click();
                }
              }}
              onClick={() =>
                setValue("title", localizedSystemTitle, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            >
              {t("common:reset")}
            </LinkTo>
          </Stack>
        )}
      </form>
    </Modal>
  );
}
